/* cspell:ignore lockd -- the module doc below quotes the founding typo (`command_rejected:exit_lockd`) verbatim */
/**
 * Occurrence producibility (R15) — "no trigger listens for an occurrence this
 * pack cannot produce": the D31 dead-content law, one level deeper.
 *
 * @remarks
 * R12's validation checks that an authored occurrence discriminant EXISTS
 * (`exit_locked` is a real rejection reason); it cannot check that anything in
 * THIS pack can ever EMIT it. Calder's stair door is the founding case: the
 * exit is `leadsOutOfPack`, the outward check runs before the lock check
 * (D19a), so its lock can only ever yield `exit_leads_outward` — a legal name,
 * loaded clean, unreachable, found only by playing.
 *
 * The check is an HONEST per-reason producibility table. Every entry is a list
 * of requirements, each answered at a named tier:
 *
 * - **`static`** — a structural fact of the pack ("at least one non-outward
 *   exit carrying a lock"), judged from the parsed document alone.
 * - **`lexicon`** — a fact of the MERGED vocabulary (the D47 pattern: `take`
 *   reaches most packs from their base), judged only when the compile
 *   succeeded; absent a lexicon, the rule abstains rather than guesses.
 *
 * A reason with no requirements is always producible and never fails. Only a
 * PROVEN-unproducible occurrence fails the load (hard, per D31 — ruled by
 * Mike, 2026-08-22), and every failure names the tier that answered.
 *
 * Honest limit, stated plainly: the judgment is PACK-SCOPED. It catches
 * "nothing in this pack can emit `exit_locked`"; it cannot catch a producible
 * reason attributed to the wrong door. And requirement predicates are sound
 * for UNPRODUCIBILITY only — holding does not promise the occurrence is
 * reachable in play (that is simulation-shaped, deferred with softlock
 * analysis), so a holding rule simply declines to condemn.
 *
 * Two authored surfaces listen for occurrences, and both are judged here with
 * the one table: trigger `on:` matchers, and `occurrence_tally_at_least`
 * keys. Tally keys are collected by a STRUCTURAL deep walk keyed on the
 * condition's discriminant rather than by enumerating condition sites —
 * conditions live in many homes (trigger guards, voice-line gates,
 * conditional-text variants) and the walk cannot go stale when they grow a
 * new one. The walk also closes R15's syntactic hole: the step-3 walker
 * checks a tally key's FAMILY prefix, and until now nothing joined the
 * `:detail` half to the discriminant lists the matchers are held to — a typo
 * (`command_rejected:exit_lockd`) loaded clean.
 *
 * @packageDocumentation
 */

import { type ContentPackDefinition, type OccurrenceMatcher } from '@orpheus/content-schema';
import { ACTION_REJECTION_REASON_TYPES, RUNTIME_ACTION_TYPES } from '@orpheus/engine-runtime';
import { type CompiledLexicon, PARSE_FAILURE_KINDS } from '@orpheus/parser-engine';

import { type ReferenceBrokenFailure } from './load-failure.js';
import { executedActionRules, type ProducibilityRule, REJECTION_REASON_RULES } from './producibility-rules.js';
import { brokenReference } from './semantic-validation.js';
import { walkDocumentObjects } from './structural-walk.js';

/** The occurrence families the engine mints tallies for (R12's four, and D235's answer to an actor's question). */
const OCCURRENCE_FAMILIES = ['command_executed', 'command_rejected', 'dialogue_answered', 'parse_failed', 'reaction_spoken'] as const;

type OccurrenceFamily = (typeof OCCURRENCE_FAMILIES)[number];

/** How one listened-for occurrence was judged. */
type ProducibilityVerdict =
	| { readonly type: 'producible' }
	| { readonly type: 'unknown_detail'; readonly universe: string }
	| { readonly type: 'unproducible'; readonly unmetRequirements: readonly ProducibilityRule[] };

/** What each family's `:detail` half must name — for the unknown-detail message. */
const DETAIL_UNIVERSE_NAMES: Readonly<Record<OccurrenceFamily, string>> = {
	command_executed: 'an engine action type (the ACTION a command executes as, not the surface verb)',
	command_rejected: 'a rejection reason the engine can emit',
	dialogue_answered: 'a prompt some actor in this pack declares',
	parse_failed: 'a parse-failure kind the parser can emit',
	reaction_spoken: 'a reaction key this pack declares',
};

/**
 * The rules for one narrowed occurrence, or `undefined` when the detail names
 * nothing in its family's universe (the syntactic hole, closed).
 */
const rulesForOccurrence = (
	family: OccurrenceFamily,
	detail: string,
	contentPack: ContentPackDefinition,
): undefined | readonly ProducibilityRule[] => {
	switch (family) {
		case 'command_executed': {
			return (RUNTIME_ACTION_TYPES as readonly string[]).includes(detail) ? executedActionRules(detail) : undefined;
		}
		case 'command_rejected': {
			return (ACTION_REJECTION_REASON_TYPES as readonly string[]).includes(detail)
				? (REJECTION_REASON_RULES[detail] ?? [])
				: undefined;
		}
		// D235: a declared prompt is answerable — the prompt layer has already
		// refused one nothing opens, so a prompt that loads is one that is asked.
		case 'dialogue_answered': {
			return contentPack.actors.some((actor) => actor.prompts?.some((prompt) => prompt.promptId === detail) === true) ? [] : undefined;
		}
		case 'parse_failed': {
			// Every documented failure kind is producible by typing (R12).
			return (PARSE_FAILURE_KINDS as readonly string[]).includes(detail) ? [] : undefined;
		}
		case 'reaction_spoken': {
			// A declared reaction is speakable by typing its exact input (D27).
			return contentPack.reactions?.[detail] === undefined ? undefined : [];
		}
	}
};

/**
 * Judge one `(family, detail)` occurrence against this pack.
 *
 * @param family - The occurrence family
 * @param detail - The narrowed half, when the author narrowed one
 * @param contentPack - The parsed pack
 * @param lexicon - The merged compiled vocabulary, when this load produced one
 * @returns The verdict — producible, unproducible with the unmet rules, or an unknown detail
 */
const judgeOccurrence = (
	family: OccurrenceFamily,
	detail: string | undefined,
	contentPack: ContentPackDefinition,
	lexicon: CompiledLexicon | undefined,
): ProducibilityVerdict => {
	// A bare family is as broad as the engine's own tally and always producible.
	if (detail === undefined) {
		return { type: 'producible' };
	}

	const rules = rulesForOccurrence(family, detail, contentPack);

	if (rules === undefined) {
		return { type: 'unknown_detail', universe: DETAIL_UNIVERSE_NAMES[family] };
	}

	const unmetRequirements = rules.filter((rule) => rule.holds(contentPack, lexicon) === false);

	return unmetRequirements.length === 0 ? { type: 'producible' } : { type: 'unproducible', unmetRequirements };
};

/** One `occurrence_tally_at_least` condition found by the structural walk. */
interface CollectedTallyKey {
	readonly path: string;
	readonly tallyKey: string;
}

/**
 * Deep-collect every `occurrence_tally_at_least` condition in the document,
 * wherever it lives. Structural on purpose (see the module remarks and
 * `structural-walk.ts`): the walk keys on the discriminant, so a new
 * condition site can never escape it.
 */
const collectTallyKeys = (contentPack: ContentPackDefinition): readonly CollectedTallyKey[] => {
	const collected: CollectedTallyKey[] = [];

	walkDocumentObjects(contentPack, (record, path) => {
		if (record['type'] === 'occurrence_tally_at_least' && typeof record['tallyKey'] === 'string') {
			collected.push({ path: `${path}.tallyKey`, tallyKey: record['tallyKey'] });
		}
	});

	return collected;
};

/** Split a tally key into its family and optional detail; `undefined` when the family is foreign. */
const parseTallyKey = (tallyKey: string): { readonly detail: string | undefined; readonly family: OccurrenceFamily } | undefined => {
	const separatorIndex = tallyKey.indexOf(':');
	const familyText = separatorIndex === -1 ? tallyKey : tallyKey.slice(0, separatorIndex);
	const family = OCCURRENCE_FAMILIES.find((candidate) => candidate === familyText);

	// A foreign family already failed the step-3 walker's prefix check; a
	// second report here would say the same thing twice.
	return family === undefined ? undefined : { detail: separatorIndex === -1 ? undefined : tallyKey.slice(separatorIndex + 1), family };
};

/** Render one verdict into a load failure at a path, or nothing for producible. */
const failureForVerdict = (
	verdict: ProducibilityVerdict,
	referencePath: string,
	occurrenceName: string,
): readonly ReferenceBrokenFailure[] => {
	switch (verdict.type) {
		case 'producible': {
			return [];
		}
		case 'unknown_detail': {
			return [brokenReference(referencePath, occurrenceName, `not ${verdict.universe}`)];
		}
		case 'unproducible': {
			const requirements = verdict.unmetRequirements
				.map((rule) => `${rule.requirement} (${rule.tier} tier)`)
				.join('; ');

			return [
				brokenReference(
					referencePath,
					occurrenceName,
					`this pack cannot produce the occurrence — requires ${requirements}`,
				),
			];
		}
	}
};

/**
 * The narrowing half of a matcher, whichever family it listens for.
 *
 * @param matcher - A trigger's `on`
 * @returns The detail, or `undefined` for a bare family
 */
const narrowedDetailOf = (matcher: OccurrenceMatcher): string | undefined => {
	switch (matcher.type) {
		case 'command_executed': {
			return matcher.intent;
		}
		case 'command_rejected': {
			return matcher.reason;
		}
		case 'dialogue_answered': {
			return matcher.promptId;
		}
		case 'parse_failed': {
			return matcher.failureKind;
		}
		case 'reaction_spoken': {
			return matcher.reactionKey;
		}
	}
};

/**
 * Judge every listened-for occurrence in the pack — trigger `on:` matchers
 * and `occurrence_tally_at_least` keys — against the producibility table.
 *
 * @remarks
 * Runs AFTER the vocabulary compile attempt (the D47 placement): lexicon-tier
 * rules judge the MERGED vocabulary, and abstain — never guess — on a load
 * whose compile failed. Static-tier rules judge either way.
 *
 * @param contentPack - The parsed, schema-valid pack
 * @param lexicon - The merged compiled vocabulary, when this load produced one
 * @returns Every unproducible or unknown occurrence found (empty = clean)
 */
export const validateOccurrenceProducibility = (
	contentPack: ContentPackDefinition,
	lexicon: CompiledLexicon | undefined,
): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];

	// The matchers: every trigger listening for a narrowed occurrence.
	contentPack.triggers.forEach((trigger, triggerIndex) => {
		const matcher = trigger.on;

		if (matcher === undefined) {
			return;
		}

		const detail = narrowedDetailOf(matcher);

		// Detail EXISTENCE is R12's check (narrative-validation) — reporting it
		// again here would double every typo — so only narrowed, known details
		// are judged for producibility.
		if (detail === undefined || rulesForOccurrence(matcher.type, detail, contentPack) === undefined) {
			return;
		}

		failures.push(
			...failureForVerdict(
				judgeOccurrence(matcher.type, detail, contentPack, lexicon),
				`triggers[${String(triggerIndex)}].on`,
				`${matcher.type}:${detail}`,
			),
		);
	});

	// The tally keys, wherever a condition reads one.
	for (const { path, tallyKey } of collectTallyKeys(contentPack)) {
		const parsedKey = parseTallyKey(tallyKey);

		if (parsedKey === undefined) {
			continue;
		}

		failures.push(
			...failureForVerdict(judgeOccurrence(parsedKey.family, parsedKey.detail, contentPack, lexicon), path, tallyKey),
		);
	}

	return failures;
};
